/**
 * UTM attribution capture.
 *
 * Captures utm_* parameters on landing, persists them for the session, and
 * writes them to the cart as note attributes so they survive navigation and the
 * checkout boundary and land on the order (Additional details). A Shopify Flow
 * then copies the 5 key params to order metafields (order.metafields.analytics.*)
 *
 * Consent: attribution is only written to the cart/order once the visitor has
 * granted analytics or marketing consent. Consent is read from OneTrust's active
 * groups (C0002 Performance / C0004 Targeting), mirroring assets/media.js and the
 * live headless frontend. Until then the values wait in sessionStorage. If no
 * OneTrust CMP is configured on the store, attribution is not gated.
 *
 * Attribution model: last-touch — a landing that carries any utm_* replaces the
 * stored set.
 */

const STORE_KEY = 'jm:utm';

/** Read utm_* params from the current URL. */
function captureFromUrl() {
  const found = {};
  try {
    const params = new URLSearchParams(window.location.search);
    params.forEach((value, key) => {
      const k = key.toLowerCase();
      if (k.indexOf('utm_') === 0 && value) found[k] = value;
    });
  } catch (error) {
    /* URL API unavailable */
  }
  return found;
}

function load() {
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}');
  } catch (error) {
    return {};
  }
}

function save(obj) {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(obj));
  } catch (error) {
    /* storage unavailable */
  }
}

/**
 * Whether analytics/marketing consent is currently granted (OneTrust groups).
 * @returns {boolean}
 */
function consentAllowed() {
  // No OneTrust CMP configured on this store -> don't gate attribution.
  if (!document.querySelector('script[data-domain-script]')) return true;
  // Grant on OneTrust "Performance" (C0002, analytics) or "Targeting" (C0004,
  // marketing), matching the live frontend's category model.
  const groups = (window.OnetrustActiveGroups || '').split(',');
  return groups.indexOf('C0002') !== -1 || groups.indexOf('C0004') !== -1;
}

let written = false;

/** sessionStorage key holding the last set we actually wrote to the cart. */
const SENT_KEY = 'jm:utm:sent';

/**
 * Write the stored utm set to the cart as note attributes — but only when it
 * differs from what we last wrote. `written` is per-page-load, so without this
 * guard the same attributes would be POSTed to /cart/update.js on every
 * navigation. The signature persists in sessionStorage across pageviews.
 */
async function writeToCart(utm) {
  const keys = Object.keys(utm);
  if (!keys.length) return;
  const attributes = {};
  keys.sort().forEach((k) => {
    attributes[k] = utm[k];
  });
  const signature = JSON.stringify(attributes);

  try {
    if (sessionStorage.getItem(SENT_KEY) === signature) {
      written = true; 
      return;
    }
  } catch (error) {
    /* storage unavailable — fall through and write */
  }

  const base = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
  try {
    await fetch(`${base}/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    written = true;
    try {
      sessionStorage.setItem(SENT_KEY, signature);
    } catch (error) {
    }
  } catch (error) {
    /* network error — will retry on the next consent/flush */
  }
}

function maybeFlush() {
  const utm = load();
  if (!Object.keys(utm).length) return;
  if (consentAllowed() === true) writeToCart(utm);
}

function init() {
  const found = captureFromUrl();
  if (Object.keys(found).length) save(found); // last-touch: replace the stored set

  maybeFlush();

  // Retry when OneTrust consent changes (banner choice or preference centre),
  // and once more after OneTrust loads late. `ot:consent` is dispatched by
  // OptanonWrapper in consent-onetrust.liquid; `OneTrustGroupsUpdated` is native.
  window.addEventListener('ot:consent', function () {
    if (!written) maybeFlush();
  });
  window.addEventListener('OneTrustGroupsUpdated', function () {
    if (!written) maybeFlush();
  });
  window.setTimeout(function () {
    if (!written) maybeFlush();
  }, 3000);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
